import React from 'react';
import { Eye, PhoneCall, TrendingUp } from 'lucide-react';
import useInView from '../hooks/useInView';

const POINTS = [
  { icon: Eye,        accent: 'accent-1', title: 'Live agent monitoring',    desc: 'Know who is on a call, who is idle and for how long — in real time.' },
  { icon: TrendingUp, accent: 'accent-4', title: 'Instant performance data', desc: 'Calls made, connect rate and conversion, updated every second.' },
  { icon: PhoneCall,  accent: 'accent-6', title: 'Campaign-level control',   desc: 'Pause, resume or reassign campaigns mid-day without disruption.' },
];

const DashboardPreviewSection = () => {
  const [ref, inView] = useInView();

  return (
  <section ref={ref} className={`section section--muted reveal ${inView ? 'is-inview' : ''}`}>
    <div className="container-page">
      <div className="grid items-center gap-14 lg:grid-cols-2">
        <div>
          <span className="eyebrow accent-6">Live dashboard</span>
          <h2 className="section-title">
            Full visibility.
            <br />
            <span className="display-accent">Real-time control.</span>
          </h2>
          <p className="section-sub max-w-xl">
            See every agent, every call and every campaign as it happens. Supervisors can monitor,
            whisper and step in without leaving the screen.
          </p>

          <div className="mt-8 space-y-4">
            {POINTS.map(point => {
              const Icon = point.icon;
              return (
                <div key={point.title} className={`${point.accent} flex items-start gap-3.5`}>
                  <span className="icon-tile icon-tile--sm mt-0.5">
                    <Icon className="h-4 w-4" />
                  </span>
                  <div>
                    <p className="font-bold text-content">{point.title}</p>
                    <p className="text-[0.9375rem] text-content-muted">{point.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* The real product, not a hand-built replica. This used to be ~80
            lines of markup imitating the dashboard, complete with invented
            agents and call counts — a drawing of the app that had to be kept
            in sync with it by hand, and that quietly presented made-up
            figures as product data. It is now the same capture the
            documentation uses, in the same browser frame. */}
        <figure className="reveal-color m-0">
          <div className="overflow-hidden bg-surface shadow-lift">
            <div className="flex items-center gap-2 border-b border-line bg-surface-muted px-4 py-3">
              <div className="flex gap-1.5">
                <span className="h-2.5 w-2.5 rounded-pill bg-danger" />
                <span className="h-2.5 w-2.5 rounded-pill bg-warning" />
                <span className="h-2.5 w-2.5 rounded-pill bg-success" />
              </div>
              <div className="mx-3 flex h-6 flex-1 items-center rounded-sm bg-surface-muted px-2">
                <span className="font-mono text-2xs text-content-muted">app.waslx.com/dashboard</span>
              </div>
            </div>

            <img
              src="/docs/dashboard.webp"
              alt="The WASLX dashboard: headline tiles for payments collected, total calls and customers closed, conversion-rate meters, and an agent performance table"
              loading="lazy"
              width="1400"
              height="840"
              className="block w-full"
            />
          </div>

          {/* Captured from the running product, not mocked up. Mobile numbers
              are masked in the capture; nothing else is altered. */}
          <figcaption className="mt-3 text-sm text-content-muted">
            The live dashboard — captured from the product, with mobile numbers masked.
          </figcaption>
        </figure>
      </div>
    </div>
  </section>
  );
};

export default DashboardPreviewSection;
